<template>
  <div class="user-role-container">
    <!-- 顶部操作区：副标题与创建按钮 -->
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-setting"></i>
          <span>{{ $t('roleManagement.userRoleListTips') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button type="primary" @click="handleCreateRole" v-if="hasPermission('sys:role:save')">
            <i class="el-icon-plus"></i> {{ $t('roleManagement.createRole') }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 权限树形结构 -->
    <div class="role-main-content">
      <el-row :gutter="20" class="full-height-row">
        <!-- 左侧角色列表 -->
        <el-col :span="8" class="full-height-col left-panel">
          <el-card class="role-list-card" shadow="hover">
            <div slot="header">
              <span>{{ $t('roleManagement.roleList') }}</span>
              <el-badge v-if="roleList.length > 0" :value="roleList.length" type="info" style="float: right;" />
            </div>
            <div class="role-list">
              <div
                v-for="role in roleList"
                :key="role.id"
                class="role-item"
                :class="{ active: selectedRole && selectedRole.id === role.id }"
                @click="selectRole(role)"
              >
                <div class="role-info">
                  <div class="role-name">
                    {{ role.roleName }}
                    <el-tag v-if="role.roleCode === 'superAdmin'" type="danger" size="mini" effect="dark" style="margin-left: 6px;">Super</el-tag>
                    <el-tag v-else-if="role.roleCode === 'admin'" type="warning" size="mini" style="margin-left: 6px;">Admin</el-tag>
                  </div>
                  <div class="role-desc">{{ role.remark }}</div>
                  <div class="role-stats">
                    <el-tag :type="role.status === 1 ? 'success' : 'danger'" size="small">
                      {{ role.status === 1 ? $t('common.active') : $t('common.inactive') }}
                    </el-tag>
                    <span class="user-count">{{ role.userCount || 0 }} {{ $t('userManagement.users') }}</span>
                  </div>
                </div>
                <div class="role-actions">
                  <el-button
                    type="text"
                    icon="el-icon-edit"
                    @click.stop="handleEditRole(role)"
                    v-if="hasPermission('sys:role:update') && role.roleCode !== 'superAdmin'"
                  ></el-button>
                  <el-button
                    type="text"
                    icon="el-icon-delete"
                    @click.stop="handleDeleteRole(role)"
                    v-if="hasPermission('sys:role:delete') && role.roleCode !== 'superAdmin'"
                  ></el-button>
                </div>
              </div>
            </div>
          </el-card>

          <el-card class="role-users-card" shadow="hover" v-if="selectedRole && selectedRole.roleCode !== 'user'">
            <div slot="header">
              <span>{{ selectedRole.roleName }} - {{ $t('userManagement.users') }}</span>
              <el-badge v-if="roleUsers.length > 0" :value="roleUsers.length" type="info" style="float: right;" />
            </div>
            <div class="role-users-list" v-loading="roleUsersLoading">
              <div v-if="roleUsers.length === 0 && !roleUsersLoading" class="empty-users-tip">
                <i class="el-icon-user"></i> {{ $t('roleManagement.noUsersInRole') }}
              </div>
              <div
                v-for="user in roleUsers"
                :key="user.userid"
                class="role-user-item"
                @click="goToUserDetail(user)"
              >
                <el-avatar :size="28" :src="getFullUrl(user.avatar)" class="user-avatar">{{ (user.username || '?')[0].toUpperCase() }}</el-avatar>
                <div class="user-info">
                  <div class="user-name">{{ user.username }}</div>
                  <div class="user-email">{{ user.email || '-' }}</div>
                </div>
                <el-tag :type="user.status === 0 ? 'info' : user.status === 2 ? 'warning' : 'success'" size="mini" effect="dark">
                  {{ user.status === 0 ? $t('common.cancelled') : user.status === 2 ? $t('common.frozen') : $t('common.inUse') }}
                </el-tag>
              </div>
            </div>
          </el-card>
        </el-col>

        <!-- 右侧权限配置 -->
        <el-col :span="16" class="full-height-col">
          <el-card class="permission-card" shadow="hover" v-if="selectedRole">
            <div slot="header">
              <span>{{ $t('roleManagement.permissionConfiguration') }} - {{ selectedRole.roleName }}</span>
              <div style="float: right; display: flex; align-items: center; gap: 8px;">
                <el-tag v-if="permissionStats.checkedPages > 0" type="success" size="small" effect="plain">
                  <i class="el-icon-document"></i> {{ permissionStats.checkedPages }} {{ $t('roleManagement.pagesSelected') }}
                </el-tag>
                <el-tag v-if="permissionStats.checkedButtons > 0" type="" size="small" effect="plain">
                  <i class="el-icon-setting"></i> {{ permissionStats.checkedButtons }} {{ $t('roleManagement.buttonsSelected') }}
                </el-tag>
                <el-button 
                  type="primary" 
                  size="small"
                  @click="handleSavePermissions" 
                  v-if="hasPermission('sys:role:update') && selectedRole.roleCode !== 'superAdmin'"
                  :loading="savingPermissions"
                  icon="el-icon-check"
                >
                  {{ $t('roleManagement.savePermissions') }}
                </el-button>
                <el-tag 
                  type="warning" 
                  v-if="selectedRole.roleCode === 'superAdmin'"
                >
                  {{ $t('roleManagement.superAdminAllPermissions') || '超级管理员拥有所有权限，不可修改' }}
                </el-tag>
              </div>
            </div>
            
            <div class="permission-body">
              <div class="permission-scroll-area" :class="{ 'is-disabled': selectedRole.roleCode === 'superAdmin' }">
                <div class="permission-section">
                  <div class="section-header">
                    <h4>{{ $t('roleManagement.pageAccessPermissions') }}</h4>
                    <div class="section-actions" v-if="selectedRole.roleCode !== 'superAdmin'">
                      <el-input
                        v-model="filterText"
                        :placeholder="$t('common.search')"
                        size="mini"
                        prefix-icon="el-icon-search"
                        class="filter-input"
                        clearable
                      ></el-input>
                      <el-button type="text" size="mini" @click="expandAllNodes">{{ isExpandedAll ? $t('roleManagement.collapseAll') : $t('roleManagement.expandAll') }}</el-button>
                      <el-button type="text" size="mini" @click="checkAllNodes">{{ isCheckedAll ? $t('roleManagement.checkNone') : $t('roleManagement.checkAll') }}</el-button>
                    </div>
                  </div>
                  <el-tree
                    ref="pagePermissionTree"
                    v-loading="loading"
                    :data="pagePermissionData"
                    show-checkbox
                    node-key="id"
                    :props="{ children: 'children', label: 'label', disabled: () => selectedRole.roleCode === 'superAdmin' }"
                    :filter-node-method="filterNode"
                    :expand-on-click-node="false"
                    :default-expanded-keys="defaultExpandedKeys"
                    @node-click="handleNodeClick"
                    @check="handlePagePermissionCheck"
                    @node-expand="handleNodeExpand"
                    @node-collapse="handleNodeCollapse"
                    highlight-current
                    class="custom-animated-tree"
                  >
                    <span class="custom-tree-node" slot-scope="{ node, data }">
                      <span class="permission-label">
                        <i :class="getNodeIcon(data)"></i>
                        {{ data.label }}
                      </span>
                      <span class="permission-node-actions">
                        <span class="permission-tag" v-if="data.description">
                          {{ data.description }}
                        </span>
                        <el-tag
                          v-if="getMenuButtonCount(data.id) > 0"
                          :type="getMenuCheckedButtonCount(data.id) === getMenuButtonCount(data.id) ? 'success' : (getMenuCheckedButtonCount(data.id) > 0 ? 'warning' : 'info')"
                          size="mini"
                          effect="plain"
                          class="button-count-tag"
                        >
                          {{ getMenuCheckedButtonCount(data.id) }}/{{ getMenuButtonCount(data.id) }}
                        </el-tag>
                      </span>
                    </span>
                  </el-tree>
                </div>
              </div>

              <div class="permission-bottom-panel" :class="{ 'is-disabled': selectedRole.roleCode === 'superAdmin' }">
                <div class="button-permission-area">
                  <div class="section-header">
                    <h4>{{ $t('roleManagement.operationButtonPermissions') }}
                      <small v-if="selectedMenuName" style="margin-left: 10px; color: #409EFF; font-weight: normal;">
                        ({{ selectedMenuName }})
                      </small>
                    </h4>
                    <div class="section-actions" v-if="selectedMenuId && buttonPermissionOptions.length > 0 && selectedRole.roleCode !== 'superAdmin'">
                      <el-button type="text" size="mini" @click="toggleMenuAllButtons">
                        {{ isAllButtonsInCurrentMenuChecked ? $t('roleManagement.checkNone') : $t('roleManagement.checkAll') }}
                      </el-button>
                    </div>
                  </div>
                  
                  <div v-if="!selectedMenuId" class="empty-buttons-tip">
                    <i class="el-icon-info"></i> {{ $t('roleManagement.selectMenuForButtons') }}
                  </div>
                  <div v-else-if="buttonPermissionOptions.length === 0" class="empty-buttons-tip">
                    <i class="el-icon-info"></i> {{ $t('roleManagement.noButtonsForMenu') }}
                  </div>
                  
                  <div class="button-checkbox-scroll" v-if="selectedMenuId && buttonPermissionOptions.length > 0">
                    <el-checkbox-group 
                      v-model="selectedButtonPermissions" 
                      @change="handleButtonPermissionChange" 
                      :disabled="selectedRole.roleCode === 'superAdmin' || !isCurrentMenuChecked"
                    >
                      <el-row :gutter="16">
                        <el-col :span="8" v-for="permission in buttonPermissionOptions" :key="permission.key" class="button-permission-col">
                          <el-checkbox :label="permission.key">
                            <div class="checkbox-content">
                              <div class="checkbox-header">
                                <span class="checkbox-label">
                                  <i :class="permission.icon"></i>
                                  {{ permission.label }}
                                </span>
                              </div>
                              <div class="permission-desc" v-if="permission.description">
                                {{ permission.description }}
                              </div>
                            </div>
                          </el-checkbox>
                        </el-col>
                      </el-row>
                    </el-checkbox-group>
                    <div v-if="!isCurrentMenuChecked" class="menu-not-checked-tip">
                      <i class="el-icon-warning"></i> {{ $t('roleManagement.menuNotCheckedTip') }}
                    </div>
                  </div>
                </div>

                <div class="data-scope-area">
                  <div class="section-header">
                    <h4>{{ $t('roleManagement.dataScope') }}</h4>
                  </div>
                  <div class="data-scope-content">
                    <el-radio-group v-model="selectedRole.dataScope" :disabled="selectedRole.roleCode === 'superAdmin'" size="small">
                      <el-radio-button :label="1">{{ $t('roleManagement.dataScopeAll') }}</el-radio-button>
                      <el-radio-button :label="2">{{ $t('roleManagement.dataScopeGroup') }}</el-radio-button>
                      <el-radio-button :label="3">{{ $t('roleManagement.dataScopeSelf') }}</el-radio-button>
                    </el-radio-group>
                    <span class="data-scope-tip">
                      <i class="el-icon-info"></i>
                      <span v-if="selectedRole.dataScope === 1">{{ $t('roleManagement.dataScopeAllTip') }}</span>
                      <span v-else-if="selectedRole.dataScope === 2">{{ $t('roleManagement.dataScopeGroupTip') }}</span>
                      <span v-else-if="selectedRole.dataScope === 3">{{ $t('roleManagement.dataScopeSelfTip') }}</span>
                    </span>
                  </div>
                </div>


              </div>
            </div>
          </el-card>

          <el-card class="permission-card" shadow="hover" v-else>
            <el-empty :description="$t('roleManagement.pleaseSelectRoleForConfig')"></el-empty>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 创建/编辑角色对话框 -->
    <el-dialog
      :title="dialogType === 'create' ? $t('roleManagement.createRoleDialogTitle') : $t('roleManagement.editRoleDialogTitle')"
      :visible.sync="roleDialogVisible"
      width="500px"
    >
      <el-form :model="roleForm" :rules="roleFormRules" ref="roleForm" label-width="80px">
        <el-form-item :label="$t('roleManagement.roleName') + ':'" prop="roleName">
          <el-input v-model="roleForm.roleName" :placeholder="$t('roleManagement.enterRoleName')"></el-input>
        </el-form-item>
        <el-form-item v-if="dialogType === 'create'" :label="$t('roleManagement.roleCode') + ':'">
          <el-input :value="$t('roleManagement.autoGenerateHint')" disabled>
            <i slot="prefix" class="el-icon-info"></i>
          </el-input>
          <div class="form-tip">{{ $t('roleManagement.roleCodeAutoGenerateTip') }}</div>
        </el-form-item>
        <el-form-item v-else :label="$t('roleManagement.roleCode') + ':'">
          <el-input :value="roleForm.roleCode || '-'" disabled></el-input>
        </el-form-item>
        <el-form-item :label="$t('roleManagement.roleDescription') + ':'" prop="remark">
          <el-input
            v-model="roleForm.remark"
            type="textarea"
            :rows="3"
            :placeholder="$t('roleManagement.enterRoleDescription')"
          ></el-input>
        </el-form-item>
        <el-form-item :label="$t('roleManagement.roleStatus') + ':'" prop="status">
          <el-radio-group v-model="roleForm.status">
            <el-radio :label="1">{{ $t('common.active') }}</el-radio>
            <el-radio :label="0">{{ $t('common.inactive') }}</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      
      <div slot="footer">
        <el-button @click="roleDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="handleRoleDialogConfirm">{{ $t('common.confirm') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { hasPermission } from '@/utils/permission'
import { getFullUrl } from '@/utils'

export default {
  name: 'UserRole',
  data() {
    return {
      selectedRole: null,
      roleDialogVisible: false,
      dialogType: 'create',
      allMenus: [],
      dynamicMenus: [],
      selectedButtonPermissions: [],
      isExpandedAll: false,
      isCheckedAll: false,
      filterText: '',
      loading: false, // 全局加载状态
      savingPermissions: false,
      selectedMenuId: null,
      selectedMenuName: '',
      defaultExpandedKeys: [],
      expandedNodeKeys: new Set(),
      roleUsers: [],
      roleUsersLoading: false,
      roleForm: {
        id: '',
        roleName: '',
        remark: '',
        status: 1
      },
      roleFormRules: {
        roleName: [
          { required: true, message: this.$t('roleManagement.roleNameRequired'), trigger: 'blur' }
        ],
        remark: [
          { required: true, message: this.$t('roleManagement.roleDescriptionRequired'), trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapGetters(['allRoles']),
    roleList() {
      return this.allRoles
    },
    pagePermissionData() {
      const HIDDEN_PERMISSIONS = ['sys:role:list', 'sys:menu:list']
      const hiddenParentIds = new Set(
        this.allMenus
          .filter(m => HIDDEN_PERMISSIONS.includes(m.permission))
          .map(m => String(m.id))
      )
      const pageMenus = this.allMenus.filter(m =>
        (m.type === 1 || m.type === 2) && !HIDDEN_PERMISSIONS.includes(m.permission)
      )
      const formattedList = pageMenus.map(item => ({
        ...item,
        id: String(item.id),
        parentId: item.parentId ? String(item.parentId) : '0',
        label: item.name,
        description: item.description || item.permission,
        permission: item.permission || '',
        url: item.url || ''
      }))
      return this.buildMenuTree(formattedList)
    },
    buttonPermissionOptions() {
      if (!this.allMenus || this.allMenus.length === 0 || !this.selectedMenuId) return []

      const selected = this.allMenus.find(m => String(m.id) === String(this.selectedMenuId))
      if (!selected) return []

      const directButtons = this.allMenus.filter(m => m.type === 3 && String(m.parentId) === String(selected.id))
      if (selected.type === 2) {
        return directButtons.map(m => ({
          key: String(m.id),
          label: m.name,
          icon: m.icon || 'el-icon-setting',
          description: m.description,
          permission: m.permission
        }))
      }

      if (selected.type === 1) {
        const menuIdsInModule = this.getDescendantMenuIds(String(selected.id))
        return this.allMenus
          .filter(m => m.type === 3 && menuIdsInModule.includes(String(m.parentId)))
          .map(m => {
            const parent = this.allMenus.find(p => String(p.id) === String(m.parentId))
            return {
              key: String(m.id),
              label: parent ? `${parent.name} / ${m.name}` : m.name,
              icon: m.icon || 'el-icon-setting',
              description: m.description,
              permission: m.permission
            }
          })
      }

      return []
    },
    isAllButtonsInCurrentMenuChecked() {
      if (!this.selectedMenuId || this.buttonPermissionOptions.length === 0) return false
      return this.buttonPermissionOptions.every(p => this.selectedButtonPermissions.includes(p.key))
    },
    isCurrentMenuChecked() {
      if (!this.selectedMenuId || !this.$refs.pagePermissionTree) return false
      const tree = this.$refs.pagePermissionTree
      const checkedKeys = tree.getCheckedKeys()
      const halfCheckedKeys = tree.getHalfCheckedKeys()
      const allKeys = [...checkedKeys, ...halfCheckedKeys]
      return allKeys.includes(String(this.selectedMenuId))
    },
    permissionStats() {
      const tree = this.$refs.pagePermissionTree
      if (!tree) return { checkedPages: 0, checkedButtons: 0 }
      const checkedKeys = tree.getCheckedKeys() || []
      const halfCheckedKeys = tree.getHalfCheckedKeys() || []
      const pageKeys = [...checkedKeys, ...halfCheckedKeys]
      return {
        checkedPages: pageKeys.length,
        checkedButtons: this.selectedButtonPermissions.length
      }
    }
  },
  watch: {
    filterText(val) {
      this.$refs.pagePermissionTree.filter(val)
    },
    selectedRole(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.syncRolePermissionsToUI(newVal)
        })
      }
    }
  },
  created() {
    this.fetchRoles()
    this.fetchMenus()
  },
  methods: {
    hasPermission,
    getFullUrl,

    syncRolePermissionsToUI(role) {
      const tree = this.$refs.pagePermissionTree
      if (!tree) return

      const isSuperAdmin = role.roleCode === 'superAdmin'

      const HIDDEN_PERMISSIONS = ['sys:role:list', 'sys:menu:list']
      const hiddenMenuIds = new Set(
        this.allMenus
          .filter(m => HIDDEN_PERMISSIONS.includes(m.permission) ||
                       HIDDEN_PERMISSIONS.some(p => { const parent = this.allMenus.find(pm => pm.permission === p); return parent && String(m.parentId) === String(parent.id) }))
          .map(m => String(m.id))
      )

      if (isSuperAdmin) {
        const allKeys = this.getAllNodeKeys(this.pagePermissionData)
        tree.setCheckedKeys(allKeys)
        this.selectedButtonPermissions = this.allMenus
          .filter(m => m.type === 3 && !hiddenMenuIds.has(String(m.id)))
          .map(m => String(m.id))
      } else {
        const menuIdList = (role.menuIdList || []).map(id => String(id))

        const pageIds = this.allMenus
          .filter(m => (m.type === 1 || m.type === 2) && menuIdList.includes(String(m.id)) && !hiddenMenuIds.has(String(m.id)))
          .map(m => String(m.id))

        this.selectedButtonPermissions = this.allMenus
          .filter(m => m.type === 3 && menuIdList.includes(String(m.id)) && !hiddenMenuIds.has(String(m.id)))
          .map(m => String(m.id))

        const leafKeys = pageIds.filter(id => {
          const node = tree.getNode(id)
          return node && node.isLeaf
        })
        tree.setCheckedKeys(leafKeys)
      }

      this.isExpandedAll = false
      this.isCheckedAll = false
      this.selectedMenuId = null
      this.selectedMenuName = ''
    },

    getNodeIcon(data) {
      if (data.icon) return data.icon
      if (data.children && data.children.length > 0) return 'el-icon-folder'
      return 'el-icon-document'
    },

    getMenuButtonCount(menuId) {
      const menu = this.allMenus.find(m => String(m.id) === String(menuId))
      if (!menu) return 0

      if (menu.type === 2) {
        return this.allMenus.filter(m => m.type === 3 && String(m.parentId) === String(menuId)).length
      }
      if (menu.type === 1) {
        const menuIdsInModule = this.getDescendantMenuIds(String(menuId))
        return this.allMenus.filter(m => m.type === 3 && menuIdsInModule.includes(String(m.parentId))).length
      }
      return 0
    },

    getMenuCheckedButtonCount(menuId) {
      const menu = this.allMenus.find(m => String(m.id) === String(menuId))
      if (!menu) return 0

      let buttonIds = []
      if (menu.type === 2) {
        buttonIds = this.allMenus.filter(m => m.type === 3 && String(m.parentId) === String(menuId)).map(m => String(m.id))
      } else if (menu.type === 1) {
        const menuIdsInModule = this.getDescendantMenuIds(String(menuId))
        buttonIds = this.allMenus.filter(m => m.type === 3 && menuIdsInModule.includes(String(m.parentId))).map(m => String(m.id))
      }
      return buttonIds.filter(id => this.selectedButtonPermissions.includes(id)).length
    },

    filterNode(value, data) {
      if (!value) return true
      return data.label.indexOf(value) !== -1 || (data.description && data.description.indexOf(value) !== -1)
    },

    handleNodeClick(data) {
      if (this.selectedRole && this.selectedRole.roleCode === 'superAdmin') return
      this.selectedMenuId = data.id
      this.selectedMenuName = data.label
    },

    handleNodeExpand(data) {
      this.expandedNodeKeys.add(data.id)
    },

    handleNodeCollapse(data) {
      this.expandedNodeKeys.delete(data.id)
    },

    handlePagePermissionCheck(data, { checkedNodes, checkedKeys, halfCheckedKeys }) {
      if (!this.selectedRole) return

      const checkedPageIds = new Set([...checkedKeys, ...halfCheckedKeys].map(id => String(id)))
      const uncheckedPageIds = this.allMenus
        .filter(m => (m.type === 1 || m.type === 2) && !checkedPageIds.has(String(m.id)))
        .map(m => String(m.id))

      const removedButtonIds = this.allMenus
        .filter(m => m.type === 3 && uncheckedPageIds.includes(String(m.parentId)))
        .map(m => String(m.id))

      if (removedButtonIds.length > 0) {
        this.selectedButtonPermissions = this.selectedButtonPermissions.filter(id => !removedButtonIds.includes(id))
      }

      this.isCheckedAll = checkedKeys.length > 0 && this.getAllNodeKeys(this.pagePermissionData).every(k => checkedKeys.includes(k))
    },

    handleButtonPermissionChange(val) {
      if (this.selectedRole) {
        this.selectedButtonPermissions = val
      }
    },

    expandAllNodes() {
      this.isExpandedAll = !this.isExpandedAll
      const nodes = this.$refs.pagePermissionTree.store._getAllNodes()
      nodes.forEach(node => {
        node.expanded = this.isExpandedAll
      })
      if (this.isExpandedAll) {
        this.expandedNodeKeys = new Set(nodes.map(n => n.data.id))
      } else {
        this.expandedNodeKeys.clear()
      }
    },

    checkAllNodes() {
      this.isCheckedAll = !this.isCheckedAll
      const tree = this.$refs.pagePermissionTree
      if (this.isCheckedAll) {
        const allKeys = this.getAllNodeKeys(this.pagePermissionData)
        tree.setCheckedKeys(allKeys)
        const HIDDEN_PERMISSIONS = ['sys:role:list', 'sys:menu:list']
        const hiddenParentIds = new Set(
          this.allMenus.filter(m => HIDDEN_PERMISSIONS.includes(m.permission)).map(m => String(m.id))
        )
        this.selectedButtonPermissions = this.allMenus
          .filter(m => m.type === 3 && !hiddenParentIds.has(String(m.parentId)))
          .map(m => String(m.id))
      } else {
        tree.setCheckedKeys([])
        this.selectedButtonPermissions = []
      }
    },

    getAllNodeKeys(data) {
      let keys = []
      data.forEach(item => {
        keys.push(item.id)
        if (item.children && item.children.length > 0) {
          keys = keys.concat(this.getAllNodeKeys(item.children))
        }
      })
      return keys
    },

    getDescendantMenuIds(rootId) {
      const idSet = new Set([String(rootId)])
      let changed = true
      while (changed) {
        changed = false
        this.allMenus.forEach(menu => {
          const parentId = String(menu.parentId || '0')
          if ((menu.type === 1 || menu.type === 2) && idSet.has(parentId) && !idSet.has(String(menu.id))) {
            idSet.add(String(menu.id))
            changed = true
          }
        })
      }
      return Array.from(idSet).filter(id => {
        const menu = this.allMenus.find(m => String(m.id) === id)
        return menu && menu.type === 2
      })
    },

    toggleMenuAllButtons() {
      const isAllChecked = this.isAllButtonsInCurrentMenuChecked
      const currentButtonKeys = this.buttonPermissionOptions.map(p => p.key)

      if (isAllChecked) {
        this.selectedButtonPermissions = this.selectedButtonPermissions.filter(key => !currentButtonKeys.includes(key))
      } else {
        const newKeys = [...new Set([...this.selectedButtonPermissions, ...currentButtonKeys])]
        this.selectedButtonPermissions = newKeys
      }
    },

    buildMenuTree(list) {
      const map = {}
      const tree = []

      list.forEach(item => {
        map[item.id] = {
          ...item,
          label: item.name || this.$t(`menu.${item.permission}`),
          children: []
        }
      })

      list.forEach(item => {
        const parent = map[item.parentId]
        if (parent) {
          parent.children.push(map[item.id])
        } else {
          tree.push(map[item.id])
        }
      })

      return tree
    },

    async fetchMenus() {
      this.loading = true
      try {
        const response = await import('@/apis/sys/menu.js').then(m => m.getMenuList())
        if (response) {
          this.allMenus = response
          const formattedList = response.map(item => ({
            ...item,
            id: String(item.id),
            parentId: item.parentId ? String(item.parentId) : '0',
            label: item.name,
            description: item.description || item.permission
          }))
          this.dynamicMenus = this.buildMenuTree(formattedList)
        }
      } catch (error) {
        console.error('获取菜单失败:', error)
        this.$message.error(this.$t('common.error'))
      } finally {
        this.loading = false
      }
    },

    async fetchRoles() {
      try {
        await this.$store.dispatch('fetchRoles')
      } catch (error) {
        this.$message.error(this.$t('roleManagement.fetchRoleError'))
      }
    },

    selectRole(role) {
      this.selectedRole = { ...role }
      this.selectedButtonPermissions = (role.menuIdList || [])
        .map(id => String(id))
        .filter(id => {
          const menu = this.allMenus.find(m => String(m.id) === id)
          return menu && menu.type === 3
        })
      this.selectedMenuId = null
      this.selectedMenuName = ''
      this.isExpandedAll = false
      this.isCheckedAll = false
      this.fetchRoleUsers(role)
    },

    async fetchRoleUsers(role) {
      if (!role || role.roleCode === 'user') {
        this.roleUsers = []
        return
      }
      this.roleUsersLoading = true
      this.roleUsers = []
      try {
        const params = { page: 1, limit: 100, roleId: role.id }
        const res = await this.$store.dispatch('fetchUsers', params)
        if (res) {
          const rawUsers = res.list || res.records || (Array.isArray(res) ? res : [])
          this.roleUsers = rawUsers.map(u => ({
            userid: String(u.id || u.userid || ''),
            username: u.username || '',
            name: u.name || u.username || '',
            avatar: u.avatar || '',
            email: u.email || '',
            status: u.status !== undefined ? (typeof u.status === 'number' ? u.status : (u.status === 'active' ? 1 : 0)) : 1
          }))
        }
      } catch (error) {
        console.error('获取角色用户列表失败:', error)
      } finally {
        this.roleUsersLoading = false
      }
    },

    goToUserDetail(user) {
      this.$router.push({ name: 'UserDetail', params: { id: user.userid } })
    },

    handleCreateRole() {
      this.dialogType = 'create'
      this.roleForm = {
        roleName: '',
        remark: '',
        status: 1
      }
      this.roleDialogVisible = true
    },

    handleEditRole(role) {
      this.dialogType = 'edit'
      this.roleForm = {
        id: role.id,
        roleName: role.roleName,
        roleCode: role.roleCode,
        remark: role.remark,
        status: role.status
      }
      this.roleDialogVisible = true
    },

    handleDeleteRole(role) {
      if (role.userCount > 0) {
        this.$confirm(
          this.$t('roleManagement.deleteRoleWithUsersConfirm', { roleName: role.roleName, userCount: role.userCount }),
          this.$t('common.warning'),
          { type: 'warning' }
        ).then(() => this.doDeleteRole(role))
        return
      }
      this.$confirm(this.$t('roleManagement.deleteRoleConfirm', { roleName: role.roleName }), this.$t('common.prompt'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(async () => {
        await this.doDeleteRole(role)
      })
    },

    async doDeleteRole(role) {
      try {
        await this.$store.dispatch('deleteRole', role.id)
        this.$message.success(this.$t('roleManagement.deleteRoleSuccess'))
        if (this.selectedRole && this.selectedRole.id === role.id) {
          this.selectedRole = null
        }
      } catch (error) {
        this.$message.error(this.$t('roleManagement.deleteRoleError'))
      }
    },

    handleRoleDialogConfirm() {
      this.$refs.roleForm.validate(async (valid) => {
        if (valid) {
          try {
            if (this.dialogType === 'create') {
              await this.$store.dispatch('createRole', this.roleForm)
              this.$message.success(this.$t('roleManagement.createRoleSuccess'))
            } else {
              const currentRole = this.allRoles.find(r => r.id === this.roleForm.id)
              const updateData = {
                id: this.roleForm.id,
                roleName: this.roleForm.roleName,
                remark: this.roleForm.remark,
                status: this.roleForm.status,
                menuIdList: currentRole ? currentRole.menuIdList : undefined
              }
              await this.$store.dispatch('updateRole', updateData)
              this.$message.success(this.$t('roleManagement.updateRoleSuccess'))
              // 同步 selectedRole
              if (this.selectedRole && this.selectedRole.id === this.roleForm.id) {
                const refreshedRole = this.$store.getters.allRoles.find(r => r.id === this.roleForm.id)
                if (refreshedRole) {
                  this.selectedRole = { ...refreshedRole }
                }
              }
            }
            this.roleDialogVisible = false
          } catch (error) {
            this.$message.error(this.dialogType === 'create' ? this.$t('roleManagement.createRoleError') : this.$t('roleManagement.updateRoleError'))
          }
        }
      })
    },

    validatePermissionsBeforeSave() {
      const tree = this.$refs.pagePermissionTree
      if (!tree) return { valid: true }

      const checkedKeys = tree.getCheckedKeys()
      const halfCheckedKeys = tree.getHalfCheckedKeys()
      const allPageKeys = [...checkedKeys, ...halfCheckedKeys].map(id => String(id))
      const checkedPageIdSet = new Set(allPageKeys)

      const orphanButtons = this.selectedButtonPermissions.filter(btnId => {
        const btnMenu = this.allMenus.find(m => String(m.id) === String(btnId))
        return btnMenu && btnMenu.type === 3 && !checkedPageIdSet.has(String(btnMenu.parentId))
      })

      if (orphanButtons.length > 0) {
        const orphanNames = orphanButtons.map(id => {
          const m = this.allMenus.find(menu => String(menu.id) === String(id))
          return m ? m.name : id
        })
        this.$message.warning(this.$t('roleManagement.orphanButtonsWarning', { buttons: orphanNames.join(', ') }))
        return { valid: false, orphanButtons }
      }

      return { valid: true }
    },

    async handleSavePermissions() {
      if (!this.selectedRole) return

      const validation = this.validatePermissionsBeforeSave()
      if (!validation.valid) {
        this.selectedButtonPermissions = this.selectedButtonPermissions.filter(id => !validation.orphanButtons.includes(id))
        await this.$nextTick()
      }

      this.savingPermissions = true
      try {
        const tree = this.$refs.pagePermissionTree
        const treeKeys = [...tree.getCheckedKeys(), ...tree.getHalfCheckedKeys()]
        const selectedPageIds = new Set(treeKeys.map(id => String(id)))
        const buttonKeys = (this.selectedButtonPermissions || []).filter(buttonId => {
          const buttonMenu = this.allMenus.find(menu => String(menu.id) === String(buttonId))
          return buttonMenu && buttonMenu.type === 3 && selectedPageIds.has(String(buttonMenu.parentId))
        })

        const HIDDEN_PERMISSIONS = ['sys:role:list', 'sys:menu:list']
        const hiddenMenuIds = this.allMenus
          .filter(m => HIDDEN_PERMISSIONS.includes(m.permission) || 
                       HIDDEN_PERMISSIONS.some(p => { const parent = this.allMenus.find(pm => pm.permission === p); return parent && String(m.parentId) === String(parent.id) }))
          .map(m => m.id)
        const existingHiddenIds = (this.selectedRole.menuIdList || []).filter(id => hiddenMenuIds.includes(id))

        const menuIdList = [...new Set([...treeKeys, ...buttonKeys, ...existingHiddenIds])]
          .map(id => Number(id))
          .filter(id => !isNaN(id))

        const updatedRole = {
          ...this.selectedRole,
          menuIdList
        }

        await this.$store.dispatch('updateRolePermissions', updatedRole)
        
        // 从刷新后的 store 中同步 selectedRole，确保后续操作基于最新数据
        const refreshedRole = this.$store.getters.allRoles.find(r => r.id === this.selectedRole.id)
        if (refreshedRole) {
          this.selectedRole = { ...refreshedRole }
          this.$nextTick(() => {
            this.syncRolePermissionsToUI(refreshedRole)
          })
        }
        
        this.$message.success(this.$t('roleManagement.savePermissionsSuccess'))

        this.$notify({
          title: this.$t('roleManagement.permissionUpdateNotifyTitle'),
          message: this.$t('roleManagement.permissionUpdateNotifyMessage'),
          type: 'success',
          duration: 5000
        })
      } catch (error) {
        console.error('保存权限失败:', error)
        this.$message.error(this.$t('roleManagement.savePermissionsError'))
      } finally {
        this.savingPermissions = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.user-role-container {
  padding: 10px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #fff;

  .control-panel {
    flex-shrink: 0;
  }

  .control-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  .header-info {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #64748b;
    font-size: 14px;
    font-weight: normal;
    
    i {
      font-size: 20px;
      color: #409eff;
    }
    
    span {
      font-size: 14px;
      font-weight: normal;
      color: #64748b;
    }
  }

  .role-main-content {
    flex: 1;
    margin-top: 10px;
    overflow: hidden;
    
    .full-height-row {
      height: 100%;
      margin: 0 !important;
    }
    
    .full-height-col {
      height: 100%;
      display: flex;
      flex-direction: column;
      padding: 0 10px !important;
      
      &:first-child { padding-left: 0 !important; }
      &:last-child { padding-right: 0 !important; }
    }
  }

  .left-panel {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .role-list-card, .permission-card {
    flex: 1;
    display: flex;
    flex-direction: column;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: none;

    &:hover {
      box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.05);
    }

    :deep(.el-card__header) {
      padding: 12px 16px;
      background-color: #f8fafc;
      border-bottom: 1px solid #f1f5f9;
      font-weight: 600;
      color: #475569;
      font-size: 14px;
    }

    :deep(.el-card__body) {
      flex: 1;
      padding: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
  }

  .role-users-card {
    flex-shrink: 0;
    max-height: 280px;
    display: flex;
    flex-direction: column;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: none;

    &:hover {
      box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.05);
    }

    :deep(.el-card__header) {
      padding: 10px 16px;
      background-color: #f8fafc;
      border-bottom: 1px solid #f1f5f9;
      font-weight: 600;
      color: #475569;
      font-size: 13px;
    }

    :deep(.el-card__body) {
      flex: 1;
      padding: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
  }

  .role-users-list {
    flex: 1;
    overflow-y: auto;
    padding: 8px 10px;
  }

  .empty-users-tip {
    text-align: center;
    padding: 20px;
    color: #94a3b8;
    font-size: 13px;

    i {
      margin-right: 4px;
    }
  }

  .role-user-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: #f1f5f9;
    }

    .user-avatar {
      flex-shrink: 0;
      background-color: #e2e8f0;
      font-size: 12px;
    }

    .user-info {
      flex: 1;
      min-width: 0;

      .user-name {
        font-size: 13px;
        font-weight: 500;
        color: #334155;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .user-email {
        font-size: 11px;
        color: #94a3b8;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  .role-list {
    flex: 1;
    overflow-y: auto;
    padding: 10px;

    .role-item {
      padding: 12px 15px;
      border-radius: 10px;
      margin-bottom: 8px;
      cursor: pointer;
      display: flex;
      justify-content: space-between; 
      align-items: center;
      transition: all 0.2s ease;
      border: 1px solid transparent;

      &:hover {
        background-color: #f8fafc;
        border-color: #f1f5f9;
      }

      &.active {
        background-color: #eff6ff;
        border-color: #3b82f6;
        .role-name { color: #2563eb; }
      }

      .role-info {
        flex: 1;
        overflow: hidden;
        
        .role-name {
          font-weight: 600;
          font-size: 14px;
          color: #1e293b;
          margin-bottom: 4px;
        }
        
        .role-desc {
          font-size: 12px;
          color: #64748b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .role-stats {
          margin-top: 6px;
          display: flex;
          align-items: center;
          gap: 10px;
          
          .user-count {
            font-size: 12px;
            color: #94a3b8;
          }
        }
      }
    }
  }

  .permission-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .permission-scroll-area {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    min-height: 0;
    
    &.is-disabled {
      opacity: 0.7;
      pointer-events: none;
    }
  }

  .permission-bottom-panel {
    flex-shrink: 0;
    border-top: 1px solid #f1f5f9;
    background-color: #fff;
    display: flex;
    flex-direction: column;

    &.is-disabled {
      opacity: 0.7;
      pointer-events: none;
    }

    .button-permission-area {
      padding: 16px 20px 0;
    }

    .data-scope-area {
      padding: 16px 20px;
      border-top: 1px solid #f1f5f9;

      .data-scope-content {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-top: 12px;

        .data-scope-tip {
          font-size: 12px;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 4px;

          i {
            color: #409eff;
          }
        }
      }
    }

    .button-checkbox-scroll {
      max-height: 180px;
      overflow-y: auto;
      padding-bottom: 8px;
    }
  }

  .permission-section {
    margin-bottom: 30px;
    
    .section-header {
      margin-bottom: 15px;
      padding-bottom: 10px;
      border-bottom: 1px solid #f1f5f9;
      
      h4 {
        margin: 0;
        font-size: 14px;
        color: #1e293b;
        font-weight: 600;
      }
    }

    .custom-tree-node {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 14px;
      padding-right: 8px;

      .permission-label {
        display: flex;
        align-items: center;
        gap: 6px;
        
        i {
          color: #64748b;
          font-size: 14px;
        }
      }

      .permission-node-actions {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-shrink: 0;
      }

      .permission-tag {
        font-size: 12px;
        color: #94a3b8;
        font-weight: normal;
        background: none;
        padding: 0;
      }

      .button-count-tag {
        font-size: 11px;
        min-width: 36px;
        text-align: center;
      }
    }

    .permission-tip {
      margin-top: 10px;
      font-size: 12px;
      color: #909399;
    }

    .empty-buttons-tip {
      padding: 30px 20px;
      text-align: center;
      color: #94a3b8;
      font-size: 13px;
      background-color: #f8fafc;
      border-radius: 8px;

      i {
        margin-right: 6px;
      }
    }

    .menu-not-checked-tip {
      margin-top: 10px;
      padding: 10px 15px;
      background-color: #fdf6ec;
      border: 1px solid #faecd8;
      border-radius: 6px;
      color: #e6a23c;
      font-size: 12px;

      i {
        margin-right: 4px;
      }
    }

    .button-permission-col {
      margin-bottom: 10px;

      :deep(.el-checkbox) {
        width: 100%;
        display: flex;
        align-items: flex-start;
        padding: 10px 12px;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        transition: all 0.25s ease;
        white-space: normal;
        background-color: #fff;

        &:hover {
          background-color: #f8fafc;
          border-color: #cbd5e1;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        }

        &.is-checked {
          background-color: #eff6ff;
          border-color: #3b82f6;
          box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.1);
        }

        &.is-disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .el-checkbox__label {
          flex: 1;
          padding-left: 10px;
        }
      }

      .checkbox-content {
        .checkbox-header {
          display: flex;
          align-items: center;
          margin-bottom: 2px;

          .checkbox-label {
            font-weight: 500;
            color: #334155;
            font-size: 13px;
            
            i {
              color: #64748b;
              margin-right: 4px;
              font-size: 13px;
            }
          }
        }

        .permission-desc {
          font-size: 11px;
          color: #94a3b8;
          line-height: 1.4;
          margin-top: 2px;
        }
      }
    }
  }
}

::v-deep {
  .el-dialog {
    border-radius: 12px;
    overflow: hidden;
    
    .el-dialog__header {
      background-color: #f8fafc;
      padding: 15px 20px;
      border-bottom: 1px solid #f1f5f9;
      
      .el-dialog__title {
        font-size: 15px;
        font-weight: 600;
        color: #1e293b;
      }
    }
    
    .el-dialog__body { padding: 20px 25px; }
    .el-dialog__footer { padding: 15px 20px; border-top: 1px solid #f1f5f9; }
    .el-input__inner, .el-textarea__inner, .el-select { border-radius: 8px; }
  }
}

@media (max-width: 768px) {
  .user-role-container {
    .el-col {
      margin-bottom: 20px;
    }
  }
}

.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
  margin-top: 5px;
  padding-left: 2px;
}
</style>
